// @flow

import React, { type Node, useState } from 'react';
import styled, { css } from 'styled-components';
import { OtpInputWithValidation } from './otp-input-six-code.js';
import { MainPanel, MainPanelContent } from '@tectonics/look-and-feel/volcanes/template';
import { MobileDisplayValues } from '@tectonics/look-and-feel/volcanes/organisms/mobile-display-values';
import { Header } from '@tectonics/look-and-feel/volcanes/organisms/header';
import { getApiBaseUrlByTenant, type Tenant } from '@tectonics/commons/tenant';
import { useLocalTenancyAsTenant } from '@tectonics/controller/context';
import { getPhoneNumber, useIncomingPayment } from '@tectonics/prs/incoming-payment';
import { Empty } from '@tectonics/components/empty';
import spinnerImageSrc from './spinner.svg';

export function OtpForm(): Node {
  const incomingPayment = useIncomingPayment();
  const phoneNumber = getPhoneNumber(incomingPayment);
  const tenant = useLocalTenancyAsTenant();
  const [errorCode, setErrorCode] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  function handleSubmit(e) {
    e.preventDefault();
    setErrorCode(false);
    const inputs = e.target.querySelectorAll('input');
    const otpCode = Array.from(inputs).map(input => input.value);

    const otpSixCode = otpCode.join('');
    const settingError = () => {
      setErrorCode(true);
      setSubmitting(false);
    };

    if (otpSixCode.length === 6) {
      setSubmitting(true);
      otpValidationRequest(incomingPayment.hash, phoneNumber, otpSixCode, tenant)
        .then((result) => {
          if (result.status_otp === 'authenticated') {
            window.location.reload();
          }
        })
        .catch(() => settingError());
    } else {
      settingError();
    }
  }

  return (
    <MainPanel>
      <Header />
      <MobileDisplayValues />
      <MainPanelContent>
        <Root>
          <Body>
            <Title>Enter the security code sent to <strong>+{phoneNumber}</strong> via sms</Title>
            <form onSubmit={ handleSubmit }>
              <OtpSection>
                <OtpInputWithValidation />
              </OtpSection>
              <Separator />
              <OtpSection>
                <span>Validate the security code </span>
              </OtpSection>
              <ButtonGroup>
                <ButtonEnterCode submitting={submitting} />
              </ButtonGroup>
              {(errorCode) ? <ErrorCode /> : <Empty />}
            </form>
          </Body>
        </Root>
      </MainPanelContent>
    </MainPanel>
  );
}

type SubmitState = {
  submitting?: boolean,
}

function ButtonEnterCode({ submitting }: SubmitState): Node {
  return (
    <ButtonValidator type="submit" data-test-id="submit">
      {submitting ? <SpinnerImage src={spinnerImageSrc} submitting={submitting} alt="loading indicator" /> : 'Enter'}
    </ButtonValidator>);
}

function ErrorCode(): Node {
  return(<WrongCode>Invalid security code</WrongCode>);
}

async function otpValidationRequest(hash, phoneNumber, otpSixCode, tenant): Promise<*> {
  const response = await performOtpValidationRequest(hash, phoneNumber, otpSixCode, tenant);
  const { payment, status, code, error, status_code, status_message } = await response.json();

  if (status !== 'SUCCESS') {
    throw new Error(`Validate Security Code From Api - ${code || status_code} ${error || status_message}`);
  }

  return payment;
}

async function performOtpValidationRequest(
  hash,
  phone_number,
  token,
  tenant: Tenant,
): Promise<Response> {
  const baseUrl = getApiBaseUrlByTenant(tenant);
  const pathUrl = `${baseUrl}ws/checkout-v2/otp?hash=${hash}`;

  const body = {
    operation: 'validate',
    hash,
    phone_number,
    token,
  };

  return fetch(pathUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify( body ),
  });
}

const Root = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.gray(20)};
`;

const Body = styled.div`
  padding: 32px 16px;
`;

const Title = styled.h1`
  font-style: normal;
  font-weight: 400;
  font-size: 18px;
  line-height: 27px;
  color: #1e272c;
  font-family: Gilroy, "Open Sans", "OpenSans", Arial, "Helvetica Neue", Helvetica, sans-serif;
  margin: 0 60px 5px 60px;
  text-align: center;
`;

const OtpSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Separator = styled.div`
  width: 454px;
  height: 0px;
  border: 1px solid #d4d9e3;
  flex: none;
  order: 0;
  flex-grow: 1;
  margin: 24px 0 24px 0;
`;

const ButtonGroup = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 24px 0 24px;
`;

const ButtonValidator = styled.button`
  color: #fff;
  display: flex;
  font-weight: 600;
  justify-content: center;
  align-items: center;
  padding: 16px 32px;
  gap: 8px;
  width: 220px;
  min-width: 220px;
  height: 46px;
  background: #0040ca;
  border-radius: 40px;
  border: 1px solid;
  flex: none;
  order: 0;
  flex-grow: 0;
`;

const WrongCode = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ff0000;
`;

const SpinnerImage = styled.img`
  height: 100%;
  display: none;
  transform: scale(3, 3);
  ${props => props.submitting && css`
    display: block;
    margin: auto;
  `}
`;
